import {
    BxfChatCanvas,
    BxfChatCanvasModeEnum,
    BxfChatCanvasWidthEnum,
} from '@blk/bxf-react-components';
import styled from '@emotion/styled';
import { useEffect } from 'react';

import { ChatFooter, ResetDialog } from '../components';
import {
    activeContentPages,
    CHAT_CANVAS_DATA_LINK_EVENTS,
    ChatFrameConstants,
    WELCOME_PAGE_HEADER_TEXT,
} from '../constants';
import { useChatBlkStore, useChatPanelFeature } from '../hooks';
import { ChatContentPage, ImportantInfoContent, RmContactPage } from '../pages';
import { useChatBlkContext } from '../providers';
import { ChatBlkBaseService } from '../services';
import { isMobileDevice } from '../utils';

const { DATA_CY_CHAT_CANVAS } = ChatFrameConstants;
const {
    CHAT,
    LOGIN,
    IMPORTANT_INFO,
    ERROR,
    LOADING,
    LOADING_ERROR,
    UNVERIFIED,
} = activeContentPages;

const ChatCanvasWrapper = styled.div`
    position: sticky;
    right: 0;
    top: 0;
    height: 100%;
    display: flex;
    justify-content: flex-end;
    // add spacer at bottom to prevent content from being cut off on scroll
    border-width: 0px;
    border-style: solid;
    width: 100%;
    pointer-events: none;
`;
const ChatCanvas = styled(BxfChatCanvas)<{ isEmbeddedPanelOpen: boolean }>`
    pointer-events: ${(props) => (props.isEmbeddedPanelOpen ? 'auto' : 'none')};
`;
const ChatCanvasContent = styled(ChatContentPage)`
    animation: fadeIn 0.6s ease-in-out forwards;
    keyframes fadeIn {
        from {
            opacity: 0;
        }
        to {
            opacity: 1;
        }
    }
`;

/**
 * ChatBlk main component that renders the chat
 * canvas and its components.
 */
const ChatFrame = ({
    hostHeaderRef,
    wrapperRef,
}: {
    hostHeaderRef?: React.RefObject<HTMLElement>;
    wrapperRef?: HTMLElement;
}) => {
    const chatService = ChatBlkBaseService.getInstance();
    const { close, chatCanvasRef } = useChatBlkContext();
    const {
        activeContent,
        isPanelOpen,
        isFullscreen,
        initialWidth,
        errorMessage,
        headerActionButtonConfig,
        recentPrompts,
        handleResetClicked,
        handleContentClosed,
        handleErrorResponse,
        handleLoginClicked,
        handlePanelOnResizing,
        handlePanelResized,
        handleRecentPromptClicked,
    } = useChatPanelFeature(chatCanvasRef, hostHeaderRef, wrapperRef);
    const { setIsFullscreen } = useChatBlkStore();
    const isMobile = isMobileDevice();

    useEffect(() => {
        setIsFullscreen(isMobile);
    }, []);

    return (
        <ChatCanvasWrapper style={{ height: '100%' }}>
            <ChatCanvas
                ref={chatCanvasRef}
                activeContent={activeContent}
                initialWidth={
                    isMobile ? BxfChatCanvasWidthEnum.XS : initialWidth.current
                }
                headerActionButtonConfig={headerActionButtonConfig}
                hasCursorOverLogo={false}
                isFullScreen={isMobile || isFullscreen}
                isEmbeddedPanelOpen={isPanelOpen}
                mode={BxfChatCanvasModeEnum.EMBEDDED}
                shouldShowCloseButton={
                    ![LOGIN, CHAT, LOADING, LOADING_ERROR, UNVERIFIED].includes(
                        activeContent
                    )
                }
                customZIndexValue='1000'
                customPages={[activeContentPages.CONTACT_RM]}
                shouldShowHeader={
                    ![
                        LOGIN,
                        IMPORTANT_INFO,
                        ERROR,
                        LOADING,
                        LOADING_ERROR,
                        UNVERIFIED,
                    ].includes(activeContent)
                }
                chatFlexDirection={'column'}
                errorMessage={chatService.isProduction ? '' : errorMessage}
                recentPrompts={recentPrompts}
                welcomePageHeaderText={WELCOME_PAGE_HEADER_TEXT}
                onLoginClicked={handleLoginClicked}
                onDismissClicked={close}
                onResetClicked={handleResetClicked}
                onEmbeddedPanelResizing={handlePanelOnResizing}
                onEmbeddedPanelResizeEnd={handlePanelResized}
                onRecentPromptClicked={handleRecentPromptClicked}
                onContentClosed={handleContentClosed}
                onUserErrorResponse={handleErrorResponse}
                data-cy={DATA_CY_CHAT_CANVAS}
                dataLinkEvent={CHAT_CANVAS_DATA_LINK_EVENTS}
            >
                <ResetDialog />
                <ChatCanvasContent />
                <RmContactPage />
                <ChatFooter />
                <ImportantInfoContent />
            </ChatCanvas>
        </ChatCanvasWrapper>
    );
};

export default ChatFrame;
